import { useEffect } from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Box,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
} from '@mui/material';

import { CompanyDepartmentDto } from '@/shared/api/companies';
import { Notification } from '@/shared/ui';
import { Dialog, TDialogBase } from '@/shared/ui/Dialog';
import { Button } from '@/shared/ui/lib/Button';
import { ControlledTextField } from '@/shared/ui/TextField';

import { addEmployeeToCompanySchema } from '../model/schemas';
import { AddEmployeeToCompanyFormValues } from '../model/types';
import { useAddEmployee } from '../model/useAddEmployee';

type AddEmployeeToCompanyProps = TDialogBase & {
  companyId: string;
  departments: CompanyDepartmentDto[];
};

const ADD_EMPLOYEE_TO_COMPANY_FORM = 'addEmployeeToCompanyForm';
const ADD_EMPLOYEE_TO_COMPANY_DIALOG = 'addEmployeeToCompanyDialog';

function departmentId(department: CompanyDepartmentDto): string {
  return department.id;
}

function isDepartmentActive(department: CompanyDepartmentDto): boolean {
  return department.is_active ?? department.isActive ?? true;
}

export const AddEmployeeToCompanyDialog = ({
  open,
  onClose,
  companyId,
  departments,
}: AddEmployeeToCompanyProps) => {
  const { t } = useTranslation();
  const {
    addEmployee,
    isPending,
    error: addEmployeeError,
    errorReset: resetErrorAddEmployeeMutation,
  } = useAddEmployee(companyId);

  const activeDepartments = departments.filter(isDepartmentActive);
  const defaultDepartmentId = activeDepartments[0]?.id ?? '';

  const {
    control,
    formState: { errors },
    getValues,
    reset,
    handleSubmit,
    setValue,
  } = useForm<AddEmployeeToCompanyFormValues>({
    resolver: zodResolver(addEmployeeToCompanySchema(t)),
    defaultValues: {
      email: '',
      firstName: '',
      lastName: '',
      departmentId: defaultDepartmentId,
      role: 'manager',
    },
    mode: 'onSubmit',
  });

  useEffect(() => {
    if (!open || !defaultDepartmentId || getValues('departmentId')) return;

    setValue('departmentId', defaultDepartmentId);
  }, [defaultDepartmentId, getValues, open, setValue]);

  const resetForm = () => {
    reset({
      email: '',
      firstName: '',
      lastName: '',
      departmentId: defaultDepartmentId,
      role: 'manager',
    });
  };

  const handleAdd: SubmitHandler<AddEmployeeToCompanyFormValues> = async (
    data
  ) => {
    await addEmployee(data, () => {
      resetForm();
      onClose?.();
    });
  };

  const handleClose = () => {
    onClose?.();
    resetForm();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      id={ADD_EMPLOYEE_TO_COMPANY_DIALOG}
      title={t('company:employeeDialog.title')}
      leftButtonSlot={
        <Button variant="outlined" onClick={handleClose}>
          {t('common:buttons.cancel')}
        </Button>
      }
      rightButtonSlot={
        <Button
          variant="contained"
          type="submit"
          form={ADD_EMPLOYEE_TO_COMPANY_FORM}
          disabled={isPending || activeDepartments.length === 0}
        >
          {t('company:buttons.addEmployee')}
        </Button>
      }
    >
      <form
        onSubmit={handleSubmit(handleAdd)}
        id={ADD_EMPLOYEE_TO_COMPANY_FORM}
      >
        <Box sx={{ mb: 2 }}>
          <ControlledTextField
            name="email"
            control={control}
            autoFocus
            error={!!errors.email}
            helperText={errors.email?.message}
            label={t('company:employeeDialog.email')}
          />
        </Box>
        <Box sx={{ mb: 2 }}>
          <Controller
            name="departmentId"
            control={control}
            render={({ field }) => (
              <FormControl fullWidth size="small" error={!!errors.departmentId}>
                <InputLabel id="employee-department-label">
                  {t('company:employeeDialog.department')}
                </InputLabel>
                <Select
                  {...field}
                  labelId="employee-department-label"
                  label={t('company:employeeDialog.department')}
                >
                  {activeDepartments.map((department) => (
                    <MenuItem key={departmentId(department)} value={departmentId(department)}>
                      {department.name}
                    </MenuItem>
                  ))}
                </Select>
                {errors.departmentId ? (
                  <FormHelperText>{errors.departmentId.message}</FormHelperText>
                ) : null}
              </FormControl>
            )}
          />
        </Box>
        <Box sx={{ mb: 2 }}>
          <Controller
            name="role"
            control={control}
            render={({ field }) => (
              <FormControl fullWidth size="small">
                <InputLabel id="employee-role-label">
                  {t('company:employeeDialog.role')}
                </InputLabel>
                <Select
                  {...field}
                  labelId="employee-role-label"
                  label={t('company:employeeDialog.role')}
                >
                  <MenuItem value="manager">
                    {t('company:employeeRoles.manager')}
                  </MenuItem>
                  <MenuItem value="department_head">
                    {t('company:employeeRoles.department_head')}
                  </MenuItem>
                </Select>
              </FormControl>
            )}
          />
        </Box>
        <Box sx={{ mb: 2 }}>
          <ControlledTextField
            name="firstName"
            control={control}
            label={t('common:inputs.firstName')}
          />
        </Box>
        <Box sx={{ mb: 2 }}>
          <ControlledTextField
            name="lastName"
            control={control}
            label={t('common:inputs.lastName')}
          />
        </Box>
      </form>

      <Notification
        open={!!addEmployeeError}
        onClose={resetErrorAddEmployeeMutation}
      >
        {addEmployeeError?.message}
      </Notification>
    </Dialog>
  );
};
